import _ from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Timeline, TimelineEvent } from 'react-event-timeline'
import ContainerDimensions from 'react-container-dimensions';
import { connect } from 'react-redux';
import { Row, Col, Glyphicon, InputGroup, SplitButton, MenuItem, Table, DropdownButton, Panel } from 'react-bootstrap';
import Button from './Button';
import ConfirmDialog from './ConfirmDialog';
import TextInput from './TextInput';
import FormField from './FormField';
import TextEditor from './TextEditor';
import CheckBox from './CheckBox';
import EventForm, { validateField } from './EventForm';
import Select from './Select';
import Multiselect from 'react-widgets/lib/Multiselect';
import SendEmailForm from './SendEmailForm';
import SendSmsForm from './SendSmsForm';
import Moment from 'moment';
import { userDownloadFile, userLogInLoadAction } from '../../actions/userActions';

import { caseSetServerError,
         caseDeleteEvent,
         faxStatusAction,
         caseGetEvents, 
         caseEditEvent, 
         caseGetSmsEvents,
         caseHashDeleteAction,
         caseFilesLoadAction} from '../../actions/caseActions';

import { dateSort,
         filterTasks,
         retrieveNotifications,
         getTodayDateTZ,
         getLocalDateTimeFromIso,
         matterPartialFormSavePromise,
         getPublicSigningUrl,
         getPublicPaymentUrl,
         getAssignedToFullName, 
         hasSignaturePlaceholders,
         isEvenPartOfInvoice
} from '../../helpers/utils';

import { colorMap } from '../../helpers/colors';
import { isEmptyObject } from '../../validators/formValidators';


// Load locale specific relative date/time messages
//
import javascriptTimeAgo from 'javascript-time-ago';
import { normalizePhone } from './../../helpers/normalizers';
import InvoiceItems from './InvoiceItems';
import EmailConversation from './EmailConversation';

import enLocale from 'javascript-time-ago/locales/en';
import 'javascript-time-ago/intl-messageformat-global';
import 'intl-messageformat/dist/locale-data/en';
javascriptTimeAgo.locale(enLocale);

const timeAgo = new javascriptTimeAgo('en-US');

// A clickable year separator rendered inside the event Timeline. It is a
// component (not a raw div) so that the `orientation` prop the Timeline injects
// into its children is absorbed here instead of leaking onto the DOM. The
// marginLeft clears the vertical timeline line (which sits at ~16px) and aligns
// the header with the event cards; font is inherited from the Timeline so it
// matches the rest of the event stream.
//
const YearHeader = ({ year, count, collapsed, onToggle }) => (
    <div
        onClick={onToggle}
        style={{
            marginLeft: 45,
            marginTop: 12,
            marginBottom: 2,
            padding: '6px 12px',
            cursor: 'pointer',
            userSelect: 'none',
            background: '#f0f0f0',
            borderRadius: 4,
            fontSize: 16,
        }}
    >
        <Glyphicon glyph={collapsed ? 'chevron-right' : 'chevron-down'} style={{ fontSize: 13, marginRight: 6 }} />
        <span style={{ fontWeight: 'normal' }}>{year != null ? year : 'Undated'}</span>
        <span style={{ color: '#888', marginLeft: 8, fontSize: 13 }}>({count} {count === 1 ? 'event' : 'events'})</span>
    </div>
);

// Collapse/expand toggle that sits on the timeline line at the very top. It
// collapses everything older than last year (we always keep this and last
// calendar year visible) or expands the whole history.
//
const HistoryToggle = ({ collapsed, onToggle }) => (
    <div style={{ position: 'relative', height: 34, marginBottom: 2 }}>
        <a
            role="button"
            onClick={onToggle}
            title={collapsed ? 'Expand full history' : 'Collapse older history'}
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                width: 32,
                height: 32,
                lineHeight: '30px',
                textAlign: 'center',
                borderRadius: '50%',
                background: '#e9f0f5',
                border: '2px solid #6fba1c',
                color: '#555',
                cursor: 'pointer',
            }}
        >
            <Glyphicon glyph={collapsed ? 'resize-full' : 'resize-small'} />
        </a>
    </div>
);

const EVENT_STREAM_TYPES = [
    {label: 'All', value: 'all'},
    {label: 'Tasks', value: 'Task'},
    {label: 'Emails', value: 'Email'},
    {label: 'Sms', value: 'Sms'},
    {label: 'Faxes', value: 'Fax'},
    {label: 'Paid', value: 'Charge'},
    {label: 'Cost', value: 'Cost'},
    {label: 'Contacted', value: 'Contated'},
    {label: 'Note', value: 'Note'},
    {label: 'Signed', value: 'Signed'},
    {label: 'Shared', value: 'Shared'},
    {label: 'Time', value: 'Time'},
];

class EventStream extends PureComponent {
    constructor(props) {
        super(props);
        this.getChargeUsedInInvoices = this.getChargeUsedInInvoices.bind(this);
        this.getEvents = this.getEvents.bind(this);
        this.getEventYear = this.getEventYear.bind(this);
        this.getCurrentYear = this.getCurrentYear.bind(this);
        this.isYearCollapsed = this.isYearCollapsed.bind(this);
        this.toggleYearCollapse = this.toggleYearCollapse.bind(this);
        this.toggleAllHistory = this.toggleAllHistory.bind(this);
        this.refreshFaxStatuses = this.refreshFaxStatuses.bind(this);
        this.deleteHash = this.deleteHash.bind(this);
        this.updateSmsTimeout = undefined;
        this.updateSmsTimeoutNo = 1;
        this.state = {
            loading: false,
            emailPreviewDialogOpen: false,
            emailReceiptDialogOpen: false,
            smsReceiptDialogOpen: false,
            deleteEventDialogOpen: false,
            chargeModificationDialogOpen: false,
            deleteEventConfirmAction: undefined,
            eventDialogOpen: false,
            eventEditField: {},
            previewedEmail: {},
            faxRefreshRequested: false,
            activitiesFilter: 'all',
            paymentUrl: undefined,
            collapsedYears: {},
        };
    }

    getEventYear(event) {
        const { loggedIn } = this.props;
        const format = process.env.DATE_FORMAT + ' HH:mm';
        let m;
        if (event['EventDate']) {
            m = Moment.tz(event['EventDate'], format, loggedIn && loggedIn['Timezone']);
        }
        if ((!m || !m.isValid()) && event['created_at']) {
            m = Moment(event['created_at']);
        }
        return (m && m.isValid()) ? m.year() : null;
    }

    getCurrentYear() {
        const tz = this.props.loggedIn && this.props.loggedIn['Timezone'];
        return tz ? Moment.tz(tz).year() : Moment().year();
    }

    // Years older than last year are collapsed by default; this and last
    // calendar year stay expanded. `collapsedYears` holds explicit user
    // overrides (true = collapsed, false = expanded) that win over the default.
    //
    defaultYearCollapsed(year) {
        return typeof year === 'number' && year < this.getCurrentYear() - 1;
    }

    isYearCollapsed(year) {
        const override = this.state.collapsedYears[year];
        if (override !== undefined) {
            return override;
        }

        return this.defaultYearCollapsed(year);
    }

    toggleYearCollapse(year) {
        const collapsed = this.isYearCollapsed(year);
        this.setState(prev => ({
            collapsedYears: { ...prev.collapsedYears, [year]: !collapsed },
        }));
    }

    // The line-top toggle: collapse every year older than last year, or expand
    // the whole history if it is already collapsed.
    toggleAllHistory(allYears, historyYears) {
        const allHistoryCollapsed = historyYears.every(y => this.isYearCollapsed(y));
        const collapsedYears = { ...this.state.collapsedYears };
        if (allHistoryCollapsed) {
            allYears.forEach(y => { collapsedYears[y] = false; });
        } else {
            historyYears.forEach(y => { collapsedYears[y] = true; });
        }

        this.setState({ collapsedYears });
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.currentCase &&
            nextProps.currentCase.id &&
            !nextProps.currentCase._loadingAllEvents &&
            !nextProps.currentCase.events) {

            this.setState({ loading: true });
            this.props.dispatch(caseGetEvents(nextProps.currentCase.id))
                .then(() => { this.setState({ loading: false }); })
                .catch((error) => {
                    this.setState({ loading: false });
                    this.props.dispatch(caseSetServerError(error));
                });

            return;
        }

        if (!this.state.faxRefreshRequested &&
            nextProps.currentCase && 
            nextProps.currentCase.id &&
            !nextProps.currentCase.faxesPoll &&
            !nextProps.currentCase._loadingFaxes) {

            this.setState({ faxRefreshRequested: true }, () => this.refreshFaxStatuses(nextProps.currentCase.id));
        }

        if (nextProps.currentCase &&
            nextProps.currentCase.id &&
            nextProps.currentCase.client &&
            nextProps.currentCase.sms) {

            const loadSms = nextProps.currentCase.sms.findIndex(v => v['Status'] !== 'Success' && !v['Status'].startsWith('Error:')) !== -1;
            const updateSms = () => {
                this.updateSmsTimeout = undefined;
                this.updateSmsTimeoutNo += 1;
                this.props.dispatch(caseGetSmsEvents(nextProps.currentCase.id, nextProps.currentCase.client['Phone']));
            };

            if (loadSms) {
                if (this.updateSmsTimeout) {
                    clearTimeout(this.updateSmsTimeout);
                }

                this.updateSmsTimeout = setTimeout(updateSms, Math.min(5000 * this.updateSmsTimeoutNo, 10 * 60 * 1000));
            } else {
                this.updateSmsTimeoutNo = 1;
            }
        }
    }

    componentWillUnmount() {
        if (this.updateSmsTimeout) {
            clearTimeout(this.updateSmsTimeout);
            this.updateSmsTimeout = undefined;
            this.updateSmsTimeoutNo = 1;
        }
    }

    deleteHash(hash, event) {
        this.setState({
            deleteEventDialogOpen: true,
            deleteEventConfirmAction: () => {
                this.props.dispatch(caseHashDeleteAction(hash))
                    .then(() => {
                        if (event && event['invoiceId']) {
                            this.props.dispatch(caseGetEvents(this.props.currentCase.id));
                        }
                        this.setState({ deleteEventDialogOpen: false });
                    })
                    .catch(error => {
                        console.error(new Error().stack, error);
                        this.props.dispatch(caseSetServerError(error));
                    });
            }
        });
    }

    getChargeUsedInInvoices(invoiceIds = []) {
        let ids = Object.keys(invoiceIds).sort().filter(i => i > 0).map(i => `#${i}`);
        if (ids.length == 0) {
            return '';
        }
        return ids.reduce((prev, curr) => prev + ', ' + curr);
    }

    getEvents(filter = undefined) {
        const {
            currentCase,
            loggedIn,
            employees,
        } = this.props;

        if (isEmptyObject(currentCase)) {
            return undefined;
        }

        // Emails
        let emails = [];
        if (currentCase.emails) {
            emails = currentCase.emails;
        }

        let emailRows = [];
        emailRows = emails.map((email) => {
            const row = {};
            let attachments = [];
            if (email['TemplateAttachments']) {
                attachments = email['TemplateAttachments'].map((attachment) => {
                    return attachment['TemplateName'];
                });
            }

            let files = [];
            if (email['FileAttachments']) {
                files = email['FileAttachments'].map((file) => {
                    return file['FileName'];
                });
            }

            const combinedAttachmentsArray = [...attachments, ...files];
            let combinedAttachments = '';
            if (combinedAttachmentsArray.length == 0) {
                combinedAttachments = '';

            } else {
                combinedAttachments = combinedAttachmentsArray.reduce((a, b) => {
                    return `${a}, ${b}`;
                });
            }

            row['EventType'] = 'Email';
            row['EventDate'] = getLocalDateTimeFromIso(loggedIn, email['created_at']) || getTodayDateTZ(loggedIn, true, true);
            row['created_at'] = email['created_at'] || row['EventDate'];
            if (combinedAttachments !== '') {
                combinedAttachments = (<span><br />Attachments: {combinedAttachments}</span>);
            }

            let emails = email['EmailTo'];
            if (_.isArray(emails)) {
                emails = emails.reduce((a, b) => `${a}, ${b}`);
            }

            row['$EventDescription$'] = (
                <div>
                Sent By: {loggedIn && getAssignedToFullName(email['OwnerId'], loggedIn, employees)}<br />
                To: {emails}<br />
                Subject: {email['Subject']}{combinedAttachments}
                </div>);

            row['EventStatus'] = 'Success';
            row['$EventEditAction$'] = (
                <Button icon="eye-open"
                        bsStyle="link"
                        onClick={() => {
                            this.setState({
                                emailPreviewDialogOpen: true,
                                emailAttachmentCount: 0,
                                // The whole conversation when it can be read, else just this email.
                                emailPreviewView: (row['EmailRowId'] && this.props.loggedIn && (this.props.loggedIn['EmailReadEnabled'] || this.props.loggedIn['GmailAccount'] || this.props.loggedIn['OutlookAccount'])) ? 'history' : 'email',
                                previewedEmail: row,
                            });
                        }}/>
            );

            row['Template'] = email['Template'];
            row['Subject'] = email['Subject'];
            row['Message'] = email['Message'];
            row['EmailTo'] = email['EmailTo'];
            row['Attachments'] = combinedAttachmentsArray;
            row['OwnerId'] = email['OwnerId'];
            row['EmailRowId'] = (email['Account'] === 'Gmail' || email['Account'] === 'Outlook') ? email['id'] : undefined;
            row['FromAddress'] = email['FromAddress'];
            if (email['WebLink']) {
                // Gmail / Outlook web link stored when the email was sent.
                row['Url'] = email['WebLink'];
            } else if ((!email['Account'] || email['Account'] == 'Gmail') && loggedIn && loggedIn.id === email['OwnerId']) {
                row['Url'] = `https://mail.google.com/mail/#inbox/${email['EmailId']}`;
            }

            return row;
        });

        // SMS

        let sms = [];
        if (currentCase.sms) {
            sms = currentCase.sms;
        }

        let smsRows = [];
        smsRows = sms.map((sms) => {
            const row = {};
            let sender = '';
            if (sms['SenderId'] && loggedIn) {
                row['$Sender$'] = getAssignedToFullName(sms['SenderId'], loggedIn, employees);
                sender = <i>Sent By: {row['$Sender$']}<br/></i>;
            }

            row['EventType'] = 'Sms';
            row['created_at'] = sms['created_at'] || row['EventDate'];
            row['EventDate'] = getLocalDateTimeFromIso(loggedIn, row['created_at']) || getTodayDateTZ(loggedIn, true, true);
            row['$EventDescription$'] = (<div><i>{sms['Direction'] === 'in' ? `Received (${normalizePhone(sms['From'])})` : `Sent (${normalizePhone(sms['To'])})`}</i><br />{sender}{(sms['Message'] || '').split('\n').map(line => <span>{line}<br /></span>)}</div>);
            row['EventStatus'] = sms['Status'];
            row['Message'] = sms['Message'];
            return row;
        });

        // Charges

        let charges = [];
        if (currentCase.charges) {
            charges = currentCase.charges;
        }

        let chargeRows = [];
        chargeRows = charges.map((charge) => {
            const row = { ...charge };
            row['EventType'] = 'Charge';
            row['$ChargeType$'] = 'charge';
            row['EventTitle'] = row['amount'] >= 0 ? 'Paid' : 'Refund';
            row['paid'] = true;
            row['amount'] = (charge['amount'] / 100.0).toFixed(2);
            row['EventDate'] = _.isString(charge['EventDate']) ? charge['EventDate'] : getLocalDateTimeFromIso(loggedIn, charge['EventDate'] || charge['created_at']);
            row['created_at'] = charge['created_at'];
            let sender = '';
            if (charge['$SenderId$'] && loggedIn) {
                row['$Sender$'] = getAssignedToFullName(charge['$SenderId$'], loggedIn, employees);
                sender = <React.Fragment>Created By: {row['$Sender$']}<br /></React.Fragment>;
            }

            const usedInInvoices = this.getChargeUsedInInvoices(charge['$InvoiceId$']);
            if (charge['EventDescription'] && charge['EventDescription'].split) {
                row['$EventDescription$'] = (
                    <React.Fragment>
                    {sender}
                    {charge['invoiceId'] && <React.Fragment>Invoice: #{charge['invoiceId']} <br/></React.Fragment>}
                    {usedInInvoices && <React.Fragment>Used in invoices: {usedInInvoices} <br/></React.Fragment>}
                    Description: {(<div>{charge['EventDescription'].split('\n').map(line => <span>{line}<br /></span>)}</div>)}<br />
                    Received: ${Math.abs(charge.amount / 100.0).toFixed(2)}<br/>
                    {charge['$AmountRemaining$'] > 0 ? <React.Fragment>Remaining for invoicing: ${Math.abs(charge['$AmountRemaining$'] / 100.0).toFixed(2)}<br/></React.Fragment> : ''}
                    Payment Method: {charge['$PaymentService$']}{charge['$TrustAccountTransfer$'] && ' - Trust account transfer'}
                    {charge['$AccountName$'] ? <span><br />Account: {charge['$AccountName$']}</span> : ''}
                    </React.Fragment>
                );
            } else {
                row['$EventDescription$'] = (
                    <React.Fragment>
                    {sender}
                    {charge['invoiceId'] && <React.Fragment>Invoice: #{charge['invoiceId']} <br/></React.Fragment>}
                    {usedInInvoices && <React.Fragment>Used in invoices: {usedInInvoices} <br/></React.Fragment>}
                    {charge.method && charge.method.name && (<React.Fragment>From: <br /></React.Fragment>)}
                    Received: {Math.abs(charge.amount / 100.0).toFixed(2)}<br />
                    {charge['$AmountRemaining$'] > 0 && <React.Fragment>Remaining for invoicing: ${Math.abs(charge['$AmountRemaining$'] / 100.0).toFixed(2)}<br/></React.Fragment>}
                    Payment Method: {charge['$PaymentService$']}{charge['$TrustAccountTransfer$'] && ' - Trust account transfer'}
                    {charge['$AccountName$'] ? <span><br />Account: {charge['$AccountName$']}</span> : ''}
                    </React.Fragment>
                );
            }

            let eventAction;
            const paymentUrl = { 'payment.PaymentLink': getPublicPaymentUrl(charge) };
            const editAllowed = !isEvenPartOfInvoice(charge);
            if (charge['$PaymentService$'] === 'Manual') {
                // TODO: every charge from 4/14/2019 should have hash, remove
                if (charge.hash && row['amount'] >= 0) {
                    let BtnComponent = '';
                    let props = {};
                    if (editAllowed) {
                        BtnComponent = SplitButton;
                        props['onClick'] = () => {
                            const event = { ...row };
                            this.setState({
                                chargeModificationDialogOpen: true,
                                eventEditField: event,
                            });
                        };
                        props['title'] = <Glyphicon glyph="edit" />;
                    } else {
                        BtnComponent = DropdownButton;
                    }
                    eventAction = (
                    <BtnComponent
                        {...props}
                        bsStyle="link"
                    >
                        <MenuItem onClick={(evt) => {
                                    evt.preventDefault();
                                    window.open(getPublicPaymentUrl(charge), '_blank');
                                }}>View Receipt</MenuItem>
                        <MenuItem onClick={(evt) => this.setState({ emailReceiptDialogOpen: true, paymentUrl })}>Email Receipt ...</MenuItem>
                        <MenuItem onClick={(evt) => this.setState({ smsReceiptDialogOpen: true, paymentUrl })}>SMS Receipt ...</MenuItem>
                    </BtnComponent>);                   
                } else {
                    eventAction = (
                        <Button icon="edit"
                                bsStyle="link"
                                disabled={!editAllowed}
                                onClick={() => {
                                    const event = { ...row };
                                    this.setState({
                                        eventDialogOpen: true,
                                        eventEditField: event,
                                    });
                                }}
                        />
                    );
                }
            // Ignore recurring charges in LawPay schedule
            } else if (charge['$PaymentService$'] != 'LawPay' || (charge['hash'] || charge['$PublicHash$']) == charge['reference']) {
                eventAction = (
                    <SplitButton onClick={(evt) => {
                                    evt.preventDefault();
                                    window.open(getPublicPaymentUrl(charge), '_blank');
                                }}
                                title={(<Glyphicon glyph="eye-open" />)}
                                bsStyle="link">
                        <MenuItem onClick={(evt) => {
                                    evt.preventDefault();
                                    window.open(getPublicPaymentUrl(charge), '_blank');
                                }}>View Receipt</MenuItem>
                        <MenuItem onClick={(evt) => this.setState({ emailReceiptDialogOpen: true, paymentUrl })}>Email Receipt ...</MenuItem>
                        <MenuItem onClick={(evt) => this.setState({ smsReceiptDialogOpen: true, paymentUrl })}>SMS Receipt ...</MenuItem>
                    </SplitButton>);
            }

            row['EventStatus'] = charge.status === 'COMPLETED' ? 'Success' : charge.status;
            row['$EventEditAction$'] = eventAction;
            row['$TrustAccountTransfer$'] = charge['$TrustAccountTransfer$'];
            row['version'] = charge['version'];
            return row;
        });

        // Faxes

        const faxRows = [];
        let faxRowsLength = 0;
        if (currentCase.faxesPoll) {
            faxRowsLength = currentCase.faxesPoll.pollResults.rows.length;
        }

        for (let i = 0; i < faxRowsLength; i += 1) {
            const fax = currentCase.faxesPoll.pollResults.rows[i];
            const row = {};
            row['EventType'] = 'Fax';
            row['EventDate'] = getLocalDateTimeFromIso(loggedIn, fax['FaxCompletedAt'] || fax['FaxRequestedAt']);
            row['created_at'] = fax['created_at'];
            let sender = '';
            if (fax['SenderId'] && loggedIn) {
                row['$Sender$'] = getAssignedToFullName(fax['SenderId'], loggedIn, employees);
                sender = <React.Fragment><br />Sent By: {row['$Sender$']}</React.Fragment>;
            }

            row['$EventDescription$'] = (<React.Fragment>To: {fax['Recipient']}<br />Fax sent: {fax['FileName']}<br/>Pages: {fax['FaxPagesNo']}{sender}</React.Fragment>);
            row['EventStatus'] = fax['FaxStatus'];
            row['$EventEditAction$'] = (
                <Button icon="download"
                        bsStyle="link"
                        onClick={() => {
                            const templateType = 'fax';
                            const templateName = 'FaxConfirmationSheet.docx';
                            const fields = {};
                            for (let property in fax) {
                                if (fax.hasOwnProperty(property)) {
                                    let value = fax[property];
                                    if (value && ['FaxCompletedAt', 'FaxRequestedAt'].includes(property)) {
                                        value = getLocalDateTimeFromIso(loggedIn, value);
                                    }

                                    fields[`docs.currentFax.${property}`] = value;
                                }
                            }

                            const extraVariables = encodeURIComponent(JSON.stringify(fields));
                            const downloadPath = `/api/v1/download/default_template/${templateType}/${templateName}?extraVariables=${extraVariables}`;
                            this.props.dispatch(userDownloadFile(downloadPath));
                        }}/>
            );

            faxRows.push(row);
        }

        // Case Events

        let caseEvents = [];
        if (currentCase.events) {
            caseEvents = currentCase.events.map((evt) => {
                const event = { ...evt };
                event['EventDate'] = _.isString(event['EventDate']) ? event['EventDate'] : getLocalDateTimeFromIso(loggedIn, event['EventDate'] || event['created_at']);
                event['$EventEditAction$'] = (
                    <Button icon="edit"
                            bsStyle="link"
                            onClick={() => {
                                this.setState({
                                    eventDialogOpen: true,
                                    eventEditField: event,
                                });
                            }}
                    />
                );

                let sender = '';
                if (event['SenderId'] && loggedIn) {
                    event['$Sender$'] = getAssignedToFullName(event['SenderId'], loggedIn, employees);
                    sender = <div>Created By: {event['$Sender$']}<br /></div>;
                }

                if (event['EventType'] === 'Time') {
                    const desc = event['EventDescription'] || '';
                    event['$EventDescription$'] = (
                    <React.Fragment>
                        {sender}
                        {event['$InvoiceId$'] && <React.Fragment>Invoice: #{event['$InvoiceId$']} <br/></React.Fragment>}
                        Time: {event['time']} minutes<br />
                        Amount: ${event['amount'] ? event['amount'] / 100.0 : 0}<br/>
                        {desc.split('\n').map(line => <span>{line}<br /></span>)}
                    </React.Fragment>);
                } else if (event['EventType'] === 'Cost') {
                    const desc = event['EventDescription'] || '';
                    const items = event['items'];
                    event['$EventDescription$'] = (
                        <div>
                            {sender}
                            {event['$InvoiceId$'] && <React.Fragment>Invoice: #{event['$InvoiceId$']} <br/></React.Fragment>}
                            {desc.split('\n').map(line => <span>{line}<br /></span>)}
                            {items && (
                                <Table striped bordered condensed hover style={{marginTop: 15, marginBottom: -5}}>
                                <thead>
                                    <tr>
                                    <td>Description</td>
                                    <td align="right">Qty</td>
                                    <td align="right">Cost</td>
                                    </tr>
                                </thead>
                                <tbody>
                                {items.map(item => <tr><td>{item.description}</td><td align="right">{item.qty || 1}</td><td align="right">${item.amount}</td></tr>)}
                                </tbody>
                                </Table>
                            )}
                        </div>);
                } else if (event['EventDescription'] && event['EventDescription'].split) {
                    event['$EventDescription$'] = (<div>{sender}{event['EventDescription'].split('\n').map(line => <span>{line}<br /></span>)}</div>);
                }

                return event;
            });
        }

        // Completed tasks
        const taskRows = [];
        if (this.props.tasks) {
            this.props.tasks.filter((task) => this.props.currentCase.id && task['CaseId'] === this.props.currentCase.id && task['Completed']).map((task) => {
                const row = {};
                row['EventType'] = 'Task';
                row['EventDate'] = getLocalDateTimeFromIso(loggedIn, task['updated_at']);
                row['$EventDescription$'] = task['Title'];
                let sender = '';
                if (task['SenderId'] && loggedIn) {
                    row['$Sender$'] = getAssignedToFullName(task['SenderId'], loggedIn, employees);
                    row['$EventDescription$'] = <div>Completed By: {row['$Sender$']}<br />{row['$EventDescription$']}</div>;
                }

                row['EventStatus'] = task['ActionError'] || 'Success';   
                // row['$EventEditIdAction$'] = (
                //     <TaskEditorField initialField={this.state.taskEditField}
                //                  tasks={tasks}
                //                  disableMove={true}
                //                  disableCheckBox={false}
                //                  editMode={true}
                //                  renderGoToCaseButton={false}
                //                  isEmptyChronoError={false}
                //                  caseType={currentCase && currentCase['CaseType']}
                //                  currentCase={currentCase}
                //                  onChange={(taskEditField) => {
                //                     this.setState({ taskEditField });
                //                  }}/>
                // )
                taskRows.push(row);
            });
        }

        let signings = [];
        if (currentCase.signings) {
            signings = currentCase.signings.map(v => {
                const eventAction = (
                    <Button onClick={(evt) => {
                                evt.preventDefault();
                                window.open(getPublicSigningUrl(v), '_blank');
                            }}
                            icon="eye-open"
                            bsStyle="link"
                    />
                );

                let title;
                let eventStatus = v.signed ? 'Success' : 'Pending';
                if (v.declined) {
                    eventStatus = 'Declined';
                }

                let eventType = 'Signed';
                const isSigning = v.placeholders === undefined || hasSignaturePlaceholders(v.placeholders);
                if (isSigning && !v.signed) {
                    title = (
                        <b style={{float: 'right', marginTop: -8, color: 'black'}}>{eventStatus} Signing
                            <Button onClick={(evt) => this.deleteHash(v.hash, v)}
                                    icon='trash'
                                    bsStyle="link"
                            />
                            {eventAction}        
                        </b>);
                } else if (!isSigning) {
                    eventStatus = 'Success';
                    eventType = 'Shared';
                    title = (
                        <b style={{float: 'right', marginTop: -8, color: 'black'}}>Shared
                            <Button onClick={(evt) => this.deleteHash(v.hash, v)}
                                    icon='trash'
                                    bsStyle="link"
                            />
                            {eventAction}        
                        </b>);
                }

                const row = {
                    EventType: eventType,
                    'EventDate': getLocalDateTimeFromIso(loggedIn, v['updated_at']),
                    '$EventEditAction$': eventAction,
                    '$EventTitle$': title,
                    'EventStatus': eventStatus,
                };

                row['$EventDescription$'] = v.signDocumentResult || v.signDocument;
                let sender = '';
                if (v['loggedId'] && loggedIn) {
                    row['$Sender$'] = getAssignedToFullName(v['loggedId'], loggedIn, employees);
                    row['$EventDescription$'] = <div>Created By: {row['$Sender$']}<br />{row['$EventDescription$']}</div>;
                }

                return row;
            });
        }

        // Pending charges
        let payments = [];
        if (currentCase.payments) {
            // Only show invoice event if there are actual invoice items, otherwise just show the charge.
            payments = currentCase.payments.filter(v => (v.paid && v.invoiceId && !isEmptyObject(v.invoiceItems) && v.invoiceItems.items && v.invoiceItems.items.length > 0) || !v.paid).map(v => {
                const eventAction = (
                    <Button onClick={(evt) => {
                                evt.preventDefault();
                                window.open(getPublicPaymentUrl(v), '_blank');
                            }}
                            icon="eye-open"
                            bsStyle="link"
                    />
                );

                let title;
                if (v.paid) {
                    title = (
                        <b style={{float: 'right', marginTop: -8, color: 'black'}}>Invoice
                            {eventAction}        
                        </b>);
                } else {
                    title = (
                        <b style={{float: 'right', marginTop: -8, color: 'black'}}>Pending Payment
                            <Button
                                onClick={evt => {
                                    this.deleteHash(v.hash, v);
                                }}
                                icon='trash'
                                bsStyle="link"
                            />
                            {eventAction}        
                        </b>);
                }

                let sender = '';
                let senderName = '';
                if (v['loggedId'] && loggedIn) {
                    senderName = getAssignedToFullName(v['loggedId'], loggedIn, employees);
                    sender = <React.Fragment>Created By: {senderName}<br /></React.Fragment>;
                }

                let desc;
                if (v.paid) {
                    desc = (
                        <React.Fragment>
                        {sender}
                        {v['invoiceId'] && <React.Fragment>Invoice: #{v['invoiceId']} <br/></React.Fragment>}
                        {v['invoiceItems'] && <InvoiceItems invoiceItems={v['invoiceItems']}/>}
                        {v.description && v.description.split && (<React.Fragment>Description: <br />{v.description.split('\n').map(line => <span>{line}<br /></span>)}<br /></React.Fragment>)}
                        </React.Fragment>);

                } else {
                    desc = (
                        <React.Fragment>
                        {sender}
                        {v['invoiceId'] && <React.Fragment>Invoice: #{v['invoiceId']} <br/></React.Fragment>}
                        {v.amount > 0 && <React.Fragment>Requested: ${(v.amount / 100.0).toFixed(2)}<br /></React.Fragment>}
                        {v.description && v.description.split && (<React.Fragment>Description: <br />{v.description.split('\n').map(line => <span>{line}<br /></span>)}<br /></React.Fragment>)}
                        {v['accountType'] && (<React.Fragment>Payment Method: {v['accountType']}<br /></React.Fragment>)}
                        {v['account'] && (<React.Fragment>Account: {_.isString(v['account']) ? v['account'] : v['account'].name}</React.Fragment>)}
                        </React.Fragment>);
                }

                const status = v.paid ? 'Success' : (<div>Pending <br /></div>);
                return {
                    EventType: 'Charge',
                    id: v['id'],
                    amount: (v['amount'] / 100.0).toFixed(2),
                    paid: v['paid'],
                    'EventDate': getLocalDateTimeFromIso(loggedIn, v['updated_at']),
                    '$EventDescription$': desc,
                    '$EventEditAction$': eventAction,
                    '$EventTitle$': title,
                    'EventStatus': status,
                    'SenderId': v['loggedId'],
                    '$Sender$': senderName,
                    '$InvoiceId$': v['$InvoiceId$'],
                    invoiceId: v.invoiceId,
                    invoiceItems: v.invoiceItems,
                    '$AmountRemaining$': v['$AmountRemaining$'],
                    '$ChargeType$': 'hash'
                };
            });
        }

        let events = [ ...emailRows, ...smsRows, ...chargeRows, ...faxRows, ...caseEvents, ...taskRows, ...signings, ...payments ];
        events = events.sort((a, b) => {
            return dateSort(a['EventDate'], b['EventDate'], () => {
                return dateSort(a['created_at'], b['created_at']);
            });
        });

        if (this.state.activitiesFilter !== 'all') {
            events = events.filter(value => value['EventType'] === this.state.activitiesFilter);
        }

        if (filter) {
            events = events.filter(value => filter.includes(value['EventType']));
        }

        return events;
    }


    refreshFaxStatuses(id) {
        if (!id &&
            this.props.currentCase &&
            this.props.currentCase.id) {

            id = this.props.currentCase.id;
        }

        if (!id) {
            this.setState({faxRefreshRequested: false});
            return;
        }

        this.setState({faxRefreshRequested: true}, () => {
            this.props.dispatch(faxStatusAction(id))
           .then(() => {
                   this.setState({
                       faxRefreshRequested: false
                   });
                   retrieveNotifications(this.props.dispatch);
               })
           .catch((error) => {
               console.error(new Error().stack, error);
               this.setState({
                   faxRefreshRequested: false
               });

               this.props.dispatch(caseSetServerError(error));
           });
        });
    }

    render() {
        let {
            currentConfig,
            currentCase,
            loggedIn,
            onSelected=false,
            selectableFilter=undefined,
            filter=undefined,
            selectedIds={}
        } = this.props;

        const events = this.getEvents(filter);
        if (!events) {
            return (<div style={{textAlign: 'center'}}>Loading ...</div>);
        }

        const editEmailTemplates = (currentConfig && currentConfig["receiptEmailTemplates"]) || [];
        const editSmsTemplates = (currentConfig && currentConfig["receiptSmsTemplates"]) || [];
        let eventStreamOptions = EVENT_STREAM_TYPES;
        if (filter) {
            eventStreamOptions = eventStreamOptions.filter(event => event.value == 'all' || filter.includes(event.value));
        }

        // Group the event stream by year. Years older than last year are
        // collapsed by default; the line-top toggle collapses/expands that
        // history (we always keep this and last calendar year visible).
        //
        const eventYears = events.map(event => this.getEventYear(event));
        const yearCounts = {};
        eventYears.forEach(year => { yearCounts[year] = (yearCounts[year] || 0) + 1; });
        const sortedYears = Object.keys(yearCounts)
            .map(year => Number(year))
            .sort((a, b) => b - a);
        const currentYear = this.getCurrentYear();
        const historyYears = sortedYears.filter(year => year < currentYear - 1);
        const hasHistory = historyYears.length > 0;
        const allHistoryCollapsed = hasHistory &&
            historyYears.every(year => this.isYearCollapsed(year));

        const emailPreviewFields = (
            <React.Fragment>
                <Row>
                <Col xs={12} md={6} style={{marginTop: 15}}>
                    <TextInput field={{
                                   name: 'PreviewEmailTo',
                                   value: this.state.previewedEmail && this.state.previewedEmail['EmailTo']
                               }}
                               label="Email To"
                               disabled
                    />
                </Col>
                </Row>
                <Row>
                <Col xs={12} md={6}>
                    <TextInput field={{
                                   name: 'PreviewSubject',
                                   value: this.state.previewedEmail && this.state.previewedEmail['Subject']
                               }}
                               label="Subject"
                               disabled
                    />
                </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <TextEditor field={{
                                        name: 'previewMessage',
                                        value: (this.state.previewedEmail && this.state.previewedEmail['Message']) || '<div/>',
                                        onChange: () => {}
                                    }}
                                    label="Message"
                                    rows={10}
                                    disabled
                                    isEmail
                                    currentConfig={currentConfig}
                                    currentCase={currentCase}
                                    loggedIn={loggedIn}
                        />
                    </Col>
                </Row>
                <Row>
                <Col xs={12} md={6}>
                    <FormField label='Attachments'>
                    <Multiselect value={this.state.previewedEmail && this.state.previewedEmail['Attachments']}
                                 disabled
                    />
                    </FormField>
                </Col>
                </Row>
            </React.Fragment>
        );

        return (
            <React.Fragment>
            <Row>
                <Col xs={12} md={8} id="tour-case-events-filter">
                    <InputGroup>
                    <InputGroup.Addon>
                        <Glyphicon glyph="filter" />
                    </InputGroup.Addon>
                    <Select field={{
                                name: 'filter',
                                value: this.state.activitiesFilter,
                                onChange: (evt) => {
                                    let filter = evt.target.value;
                                    this.setState({activitiesFilter: filter});
                                }
                            }}
                            name="filter"
                            options={eventStreamOptions}
                    />
                    </InputGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    {events.length > 0 && (
                    <Timeline>
                    {(() => {
                    const nodes = [];
                    if (hasHistory) {
                        nodes.push(
                            <HistoryToggle
                                key="history-toggle"
                                collapsed={allHistoryCollapsed}
                                onToggle={() => this.toggleAllHistory(sortedYears, historyYears)}
                            />
                        );
                    }

                    let lastYear;
                    events.forEach((event, index) => {
                        const year = this.getEventYear(event);
                        if (year !== lastYear) {
                            lastYear = year;
                            const collapsed = this.isYearCollapsed(year);
                            const count = yearCounts[year];
                            nodes.push(
                                <YearHeader
                                    key={`year-${year}`}
                                    year={year}
                                    count={count}
                                    collapsed={collapsed}
                                    onToggle={() => this.toggleYearCollapse(year)}
                                />
                            );
                        }

                        if (this.isYearCollapsed(year)) {
                            return;
                        }

                        let glyph;
                        let color;
                        switch (event['EventType']) {
                        case 'Email':
                            glyph = 'envelope';
                            color = `rgba(${colorMap['yellow']}, 0.4)`;
                            break;

                        case 'Sms':
                            glyph = 'phone';
                            color = `rgba(${colorMap['blue']}, 0.4)`;
                            break;

                        case 'Signed':
                        case 'Shared':
                            glyph = 'pencil';
                            color = `rgba(${colorMap['brown']}, 0.4)`;
                            break;

                        case 'Fax':
                            glyph = 'phone-alt';
                            color = `rgba(${colorMap['orange']}, 0.4)`;
                            break;

                        case 'Charge':
                        case 'Cost':
                            glyph = 'usd';
                            color = `rgba(${colorMap['teal']}, 0.4)`;
                            break;

                        case 'Note':
                            glyph = 'comment';
                            color = `rgba(${colorMap['beige']}, 0.4)`;
                            break;

                        case 'Contacted':
                            glyph = 'earphone';
                            color = `rgba(${colorMap['olive']}, 0.4)`;
                            break;

                        case 'Task':
                            glyph = 'ok';
                            color = `rgba(${colorMap['maroon']}, 0.4)`;
                            break;

                        case 'Time':
                            glyph = 'time';
                            color = `rgba(${colorMap['navy']}, 0.4)`;
                            break;

                        default:
                            assert(false);
                        }

                        let date;
                        const format = process.env.DATE_FORMAT + ' HH:mm';
                        if (event['EventDate']) {
                            date = Moment.tz(event['EventDate'], format, loggedIn && loggedIn['Timezone']);
                        }

                        let dateStr = '';
                        if (date) {
                            dateStr = date.format(format);
                            dateStr += ` (${timeAgo.format(date.toDate())})`;
                        }

                        const errorColor = `rgba(${colorMap['red']}, 0.5)`;
                        let status = '';
                        if (event['EventStatus'] !== 'Success') {
                            status = event['EventStatus'];
                        }

                        let title = event['$EventTitle$'];
                        if (!title) {
                            title = <b style={{ float: 'right', marginTop: event['$EventEditAction$'] ? -10 : 0 }}>{event['EventTitle'] || event['EventType']}{event['$EventEditAction$']}</b>;
                        }

                        let icon = '';
                        if (onSelected) {
                            icon = (
                                <React.Fragment>
                                <CheckBox
                                    field={{name: 'eventCheckBox' + index}}
                                    checked={!isEmptyObject(selectedIds[event.id])}
                                    style={{marginTop: 25, marginLeft: -32, paddingRight: 9}}
                                    disabled={selectableFilter && !selectableFilter(event)}
                                    onChange={_ => onSelected(event)}
                                />
                                <Glyphicon glyph={glyph} />
                                </React.Fragment>);
                        } else {
                            icon = <Glyphicon glyph={glyph} />;
                        }                        

                        nodes.push(
                            <TimelineEvent
                                key={event.id || index}
                                title={(<h6 style={{marginTop: 2}}>{dateStr}{title}</h6>)}
                                icon={icon}
                                container="card"
                                cardHeaderStyle={{height: '40px', backgroundColor: event['EventStatus'] === 'Success' ? color : errorColor, color: '#000'}}
                            >
                            <div style={{fontSize: 13, marginBottom: 0}}>{status && <b style={{color: 'red'}}>{status}<br /></b>}{event['$EventDescription$']}</div>
                            </TimelineEvent>);
                    });
                    return nodes;
                    })()}
                    </Timeline>)}
                </Col>
            </Row>
            <SendEmailForm 
                showAsDialog
                initialTemplate={editEmailTemplates && {...editEmailTemplates[0]}}
                templatesFieldName="receiptEmailTemplates"
                renderTemplateLoad={false}
                renderEmailSection
                renderEmailButton
                disableCaseFilesAutoload
                renderAttachmentField={false}
                onEmailSent={() => this.setState({ emailReceiptDialogOpen: false })}
                cancel={() => this.setState({ emailReceiptDialogOpen: false })}
                open={this.state.emailReceiptDialogOpen}
                title="Email Receipt"
                extraPlaceholders={this.state.paymentUrl}
            />
            <SendSmsForm
                showAsDialog
                templates={editSmsTemplates}
                initialTemplate={editSmsTemplates && {...editSmsTemplates[0]}}
                templatesFieldName="paySmsTemplates"
                renderTemplateLoad={false}
                onSmsSent={() => this.setState({ smsReceiptDialogOpen: false })}
                cancel={() => this.setState({ smsReceiptDialogOpen: false })}
                open={this.state.smsReceiptDialogOpen}
                title="SMS Receipt"
                extraPlaceholders={this.state.paymentUrl}
            />
            <ConfirmDialog
                title='Event Delete Confirmation'
                text='Do you really want to permanently delete this event?'
                cancel={() => {this.setState({deleteEventDialogOpen: false})}}
                open={this.state.deleteEventDialogOpen}
                confirm={this.state.deleteEventConfirmAction}
            />
            <ConfirmDialog
                title={this.state.eventEditField && isEvenPartOfInvoice(this.state.eventEditField) ? `Event Edit (Invoice #${this.state.eventEditField['$InvoiceId$']})` : 'Event Edit'}
                cancel={() => {this.setState({eventDialogOpen: false})}}
                open={this.state.eventDialogOpen}
                size='large'
            >
                <Row>
                <Col xs={12}>
                    <EventForm
                        initialField={this.state.eventEditField}
                        disableEventTypeEditing
                        showDateTime
                        onChange={(eventEditField) => {
                            this.setState({eventEditField});
                        }}/>
                </Col>
                </Row>
                <Row>
                <Col xs={12}>
                <Button style={{ float: 'right', marginLeft: 25 }}
                        label="Save"
                        icon="floppy-disk"
                        bsStyle="primary"
                        loading={this.state.loadingEvents}
                        disabled={this.state.loadingEvents || isEvenPartOfInvoice(this.state.eventEditField)}
                        onClick={() => {0
                            const event = validateField(this.state.eventEditField);
                            if (!isEmptyObject(event.error)) {
                                this.setState({ eventEditField: event });
                                return;
                            }
                            if (event['EventType'] === 'Charge') {
                                event['amount'] = Math.trunc(event['amount'] * 100);
                            } else if (event['EventType'] === 'ChargeRefund') {
                                event['amount'] = - Math.trunc(event['amount'] * 100);
                            }
                            this.setState({ loadingEvents: true });
                            this.props.dispatch(caseEditEvent(event))
                                .then(() => {
                                        // Resave will trigger Time recalculation
                                        if (event['EventType'] === 'Time') {
                                            matterPartialFormSavePromise(
                                                this.props.dispatch, 
                                                parseInt(this.props.currentCase.id),
                                                this.props.currentCase).then(() => {
                                                    this.setState({loadingEvents: false, eventDialogOpen: false});
                                                });
                                        } else if (event['EventType'] == 'Charge') {
                                            this.props.dispatch(caseGetEvents(this.props.currentCase.id))
                                            .then(() => { this.setState({ loadingEvents: false, eventDialogOpen: false }); })
                                            .catch((error) => {
                                                this.setState({ loadingEvents: false, eventDialogOpen: false });
                                                this.props.dispatch(caseSetServerError(error));
                                            });
                                        } else {
                                            this.setState({
                                                eventDialogOpen: false,
                                                loadingEvents: false
                                            });
                                        }
                                    }).catch((error) => {
                                        console.error(new Error().stack, error);
                                        this.props.dispatch(caseSetServerError(error));
                                        this.setState({ loadingEvents: false });
                                    });
                        }}/>
                <Button style={{float: 'right'}}
                        icon="trash"
                        label="Delete"
                        disabled={this.state.loadingEvents || isEvenPartOfInvoice(this.state.eventEditField)}
                        onClick={() => {
                            let event = {...this.state.eventEditField};
                            this.setState({loadingEvents: true});
                            this.props.dispatch(caseDeleteEvent(event.id, event['EventType'], event.version))
                                .then(() => {
                                    // Resave will trigger Time recalculation
                                    if (event['EventType'] === 'Time') {
                                        matterPartialFormSavePromise(
                                            this.props.dispatch, 
                                            parseInt(this.props.currentCase.id),
                                            this.props.currentCase).then(() => {
                                                this.setState({loadingEvents: false, eventDialogOpen: false});
                                            });
                                    } else if (event['EventType'] == 'Charge') {
                                        this.props.dispatch(caseGetEvents(this.props.currentCase.id))
                                        .then(() => { this.setState({ loadingEvents: false, eventDialogOpen: false }); })
                                        .catch((error) => {
                                            this.setState({ loadingEvents: false, eventDialogOpen: false });
                                            this.props.dispatch(caseSetServerError(error));
                                        });
                                    } else {
                                        this.setState({loadingEvents: false, eventDialogOpen: false});
                                    }
                                })
                                .catch(error => {
                                    console.error(new Error().stack, error);
                                    this.props.dispatch(caseSetServerError(error));
                                    this.setState({loadingEvents: false});
                                });
                            }}/>
                </Col>
                </Row>
            </ConfirmDialog>
            <ConfirmDialog title={(this.state.previewedEmail && this.state.previewedEmail['Subject']) || 'Email Preview'}
                           hideCancelButton
                           cancel={() => {this.setState({emailPreviewDialogOpen: false})}}
                           open={this.state.emailPreviewDialogOpen}
                           size="large"
                           confirmButton={(this.state.previewedEmail['Url'] || (this.state.emailAttachmentCount > 0 && this.state.emailPreviewView !== 'email')) && (
                               <span>
                               {this.state.emailAttachmentCount > 0 && this.state.emailPreviewView !== 'email' && (
                                   <span style={{ marginRight: 8 }}>
                                   <Button label="Save all attachments"
                                           bsStyle="primary"
                                           icon="floppy-disk"
                                           onClick={() => this.emailConversationRef && this.emailConversationRef.openSaveAll()}
                                   /></span>)}
                               {this.state.previewedEmail['Url'] && (
                               <Button label="Open in Webmail"
                                       bsStyle="primary"
                                       icon="eye-open"
                                       onClick={() => {
                                           window.open(this.state.previewedEmail['Url'])
                                       }}
                               />)}
                               </span>
                           )}
            >
                {(() => {
                    const canShowHistory = !!(this.state.previewedEmail['EmailRowId'] && currentCase);
                    const showHistory = canShowHistory && this.state.emailPreviewView !== 'email';
                    return (
                        <div>
                        {this.state.previewedEmail['FromAddress'] && (
                            <div className="ct-sending-from">Sent from <strong>{this.state.previewedEmail['FromAddress']}</strong></div>
                        )}
                        <div className="ct-email-toggle" role="group" aria-label="What to show">
                            <button type="button"
                                    className={showHistory ? '' : 'is-active'}
                                    aria-pressed={!showHistory}
                                    onClick={() => this.setState({ emailPreviewView: 'email' })}>This email</button>
                            <button type="button"
                                    className={showHistory ? 'is-active' : ''}
                                    aria-pressed={showHistory}
                                    disabled={!canShowHistory}
                                    title={canShowHistory ? 'Replies and attachments of the whole conversation' : 'Available for emails sent from Case Tempo once "Read email conversations" is on in your profile'}
                                    onClick={() => this.setState({ emailPreviewView: 'history' })}>Full history</button>
                        </div>
                        {showHistory ? (
                            <EmailConversation key={this.state.previewedEmail['EmailRowId']}
                                               ref={ref => { this.emailConversationRef = ref; }}
                                               onAttachmentCount={count => this.setState({ emailAttachmentCount: count })}
                                               caseId={currentCase.id}
                                               emailId={this.state.previewedEmail['EmailRowId']}
                                               sender={loggedIn && loggedIn.id === this.state.previewedEmail['OwnerId'] ? loggedIn['Email'] : undefined}
                                               provider={loggedIn && loggedIn['GmailAccount'] ? 'Gmail' : 'Outlook'}
                                               onEnabled={() => this.props.dispatch(userLogInLoadAction())}
                                               onAttachmentSaved={() => this.props.dispatch(caseFilesLoadAction(currentCase.id))}
                                               fallback={<div style={{padding: 15}}>{emailPreviewFields}</div>} />
                        ) : (<div style={{padding: 15}}>{emailPreviewFields}</div>)}
                        </div>
                    );
                })()}
            </ConfirmDialog>
            <ConfirmDialog title="Warning: Charge Modification"
                           warning
                           cancel={() => {this.setState({ chargeModificationDialogOpen: false, eventEditField: undefined })}}
                           confirm={() => {
                               this.setState({ chargeModificationDialogOpen: false, eventDialogOpen: true });
                           }}
                           disabled={!this.state.eventEditField || isEvenPartOfInvoice(this.state.eventEditField)}
                           confirmLabel="Modify Charge"
                           text="Modifying this charge will invalidate receipt tied to this charge, including receipt sent as a link. Do you wish to continue?"
                           open={this.state.chargeModificationDialogOpen}/>
            </React.Fragment>
        );
    }
}

class _EventStream extends PureComponent {
    render() {
        return (
            <ContainerDimensions>
                <EventStream {...this.props}/>
            </ContainerDimensions>);
    }
}

_EventStream.propTypes = {
    filter: PropTypes.array,
    onSelected: PropTypes.func,
    selectedIds: PropTypes.object,
    selectableFilter: PropTypes.func
};

const mapStateToProps = (state, ownProps) => {
    const currentCase = state.cases.current;
    const tasks = filterTasks(state.user.tasks, currentCase && currentCase.id, false);

    return {
        currentConfig: state.user.config,
        currentCase,
        loggedIn: state.user.loggedIn,
        employees: state.user.employees,
        tasks,
    }
}

export default connect(mapStateToProps)(_EventStream);